// frontend/src/pages/CamDoBrainSetup.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import camdobrainService from '../services/camdobrainService';
import instagramService from '../services/instagramService';
import Loading from '../components/Loading';
import './CamDoBrainSetup.css';

const CamDoBrainSetup = () => {
  const navigate = useNavigate();
  const { brandId } = useParams();
  const isEditing = !!brandId;

  // Form state
  const [formData, setFormData] = useState({
    account_id: '',
    brand_name: '',
    industry: '',
    brand_voice: '',
    brand_values: [],
    target_audience: {
      age_range: '',
      gender: '',
      interests: [],
      location: '',
    },
    visual_guidelines: {
      primary_color: '#8b5cf6',
      secondary_color: '#f1f5f9',
      style: '',
      fonts: '',
    },
    content_pillars: [],
    keywords: [],
  });

  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const [competitors, setCompetitors] = useState([]); // TrackedAccounts seleccionados como competidores
  const [availableTrackedAccounts, setAvailableTrackedAccounts] = useState([]); // TrackedAccounts disponibles
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);

  // Input helpers
  const [newValue, setNewValue] = useState('');
  const [newPillar, setNewPillar] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [newInterest, setNewInterest] = useState('');
  const [selectedCompetitorId, setSelectedCompetitorId] = useState('');

  const industries = [
    'Tecnologia',
    'Moda',
    'Gastronomia',
    'Fitness',
    'Belleza',
    'Educacion',
    'Entretenimiento',
    'Salud',
    'Finanzas',
    'Viajes',
    'Arte',
    'Musica',
    'Deportes',
    'Hogar',
    'Mascotas',
    'Automotriz',
    'Retail',
    'Servicios',
    'Otro',
  ];

  useEffect(() => {
    loadInitialData();
  }, [brandId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Load Instagram accounts - handle paginated response
      const accountsRes = await instagramService.getAccounts();
      const accounts = Array.isArray(accountsRes.data)
        ? accountsRes.data
        : (accountsRes.data?.results || []);
      setInstagramAccounts(accounts);

      // Load available tracked accounts from Instagram Tracker (for competitors)
      try {
        const trackedRes = await camdobrainService.getTrackedAccounts();
        const trackedAccounts = Array.isArray(trackedRes.data)
          ? trackedRes.data
          : (trackedRes.data?.results || []);
        setAvailableTrackedAccounts(trackedAccounts);
      } catch (err) {
        console.log('No tracked accounts available');
        setAvailableTrackedAccounts([]);
      }

      // If editing, load brand data
      if (brandId) {
        const brandRes = await camdobrainService.getBrandProfile(brandId);
        const brand = brandRes.data;
        setFormData({
          account_id: brand.account || brand.account_id || '',
          brand_name: brand.brand_name || '',
          industry: brand.industry || '',
          brand_voice: brand.brand_voice || '',
          brand_values: brand.brand_values || [],
          target_audience: brand.target_audience || {
            age_range: '',
            gender: '',
            interests: [],
            location: '',
          },
          visual_guidelines: brand.visual_guidelines || {
            primary_color: '#8b5cf6',
            secondary_color: '#f1f5f9',
            style: '',
            fonts: '',
          },
          content_pillars: brand.content_pillars || [],
          keywords: brand.keywords || [],
        });
        // Los competidores ahora son TrackedAccounts
        setCompetitors(brand.competitors || []);
      }

    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value,
      },
    }));
  };

  const addToArray = (field, value, setValue) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()],
      }));
      setValue('');
    }
  };

  const removeFromArray = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const addInterest = () => {
    if (newInterest.trim()) {
      setFormData(prev => ({
        ...prev,
        target_audience: {
          ...prev.target_audience,
          interests: [...(prev.target_audience.interests || []), newInterest.trim()],
        },
      }));
      setNewInterest('');
    }
  };

  const removeInterest = (index) => {
    setFormData(prev => ({
      ...prev,
      target_audience: {
        ...prev.target_audience,
        interests: prev.target_audience.interests.filter((_, i) => i !== index),
      },
    }));
  };

  const addCompetitor = () => {
    if (!selectedCompetitorId) return;

    // Encontrar la cuenta rastreada seleccionada
    const trackedAccount = availableTrackedAccounts.find(
      acc => acc.id === parseInt(selectedCompetitorId)
    );

    if (trackedAccount && !competitors.find(c => c.id === trackedAccount.id)) {
      setCompetitors(prev => [...prev, trackedAccount]);
    }

    setSelectedCompetitorId('');
  };

  const removeCompetitor = (competitorId) => {
    // Solo quitar de la lista local, no eliminar la cuenta rastreada
    setCompetitors(prev => prev.filter(c => c.id !== competitorId));
  };

  // Filtrar cuentas disponibles (excluir las ya seleccionadas)
  const getAvailableCompetitors = () => {
    const selectedIds = competitors.map(c => c.id);
    return availableTrackedAccounts.filter(acc => !selectedIds.includes(acc.id));
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError(null);

      // Prepare data - convert account_id to account for the API
      const { account_id, ...restFormData } = formData;
      const data = {
        ...restFormData,
        account: parseInt(account_id, 10), // Backend expects integer PK
        competitor_ids: competitors.map(c => c.id),
      };

      if (isEditing) {
        await camdobrainService.updateBrandProfile(brandId, data);
      } else {
        await camdobrainService.createBrandProfile(data);
      }

      navigate('/camdobrain');
    } catch (err) {
      console.error('Error saving:', err);
      setError(err.response?.data?.error || 'Error al guardar perfil de marca');
    } finally {
      setSaving(false);
    }
  };

  const renderStep1 = () => (
    <div className="setup-step">
      <h2>1. Informacion Basica</h2>
      <p className="step-description">
        Conecta una cuenta de Instagram y define la identidad de tu marca.
      </p>

      <div className="form-group">
        <label>Cuenta de Instagram *</label>
        <select
          name="account_id"
          value={formData.account_id}
          onChange={handleInputChange}
          required
        >
          <option value="">Seleccionar cuenta...</option>
          {instagramAccounts.map(acc => (
            <option key={acc.id} value={acc.id}>
              @{acc.username} ({acc.media_count || 0} posts)
            </option>
          ))}
        </select>
        {instagramAccounts.length === 0 && (
          <p className="helper-text">
            No tienes cuentas de Instagram conectadas.
            <a href="/instagram">Conectar cuenta</a>
          </p>
        )}
      </div>

      <div className="form-group">
        <label>Nombre de la Marca *</label>
        <input
          type="text"
          name="brand_name"
          value={formData.brand_name}
          onChange={handleInputChange}
          placeholder="Ej: Mi Empresa"
          required
        />
      </div>

      <div className="form-group">
        <label>Industria / Rubro *</label>
        <select
          name="industry"
          value={formData.industry}
          onChange={handleInputChange}
          required
        >
          <option value="">Seleccionar industria...</option>
          {industries.map(ind => (
            <option key={ind} value={ind}>{ind}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Voz de Marca</label>
        <textarea
          name="brand_voice"
          value={formData.brand_voice}
          onChange={handleInputChange}
          placeholder="Describe como habla tu marca. Ej: Cercana, profesional pero amigable, usa humor ocasional..."
          rows={4}
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="setup-step">
      <h2>2. Valores y Audiencia</h2>
      <p className="step-description">
        Define los valores de tu marca y tu audiencia objetivo.
      </p>

      <div className="form-group">
        <label>Valores de Marca</label>
        <div className="tag-input">
          <input
            type="text"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            placeholder="Agregar valor (ej: Innovacion)"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('brand_values', newValue, setNewValue))}
          />
          <button type="button" onClick={() => addToArray('brand_values', newValue, setNewValue)}>
            +
          </button>
        </div>
        <div className="tags">
          {formData.brand_values.map((value, i) => (
            <span key={i} className="tag">
              {value}
              <button type="button" onClick={() => removeFromArray('brand_values', i)}>&times;</button>
            </span>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label>Rango de Edad de Audiencia</label>
        <input
          type="text"
          value={formData.target_audience.age_range}
          onChange={(e) => handleNestedChange('target_audience', 'age_range', e.target.value)}
          placeholder="Ej: 25-45 anos"
        />
      </div>

      <div className="form-group">
        <label>Genero Principal</label>
        <select
          value={formData.target_audience.gender}
          onChange={(e) => handleNestedChange('target_audience', 'gender', e.target.value)}
        >
          <option value="">Seleccionar...</option>
          <option value="female">Mayoria Mujeres</option>
          <option value="male">Mayoria Hombres</option>
          <option value="mixed">Mixto</option>
        </select>
      </div>

      <div className="form-group">
        <label>Ubicacion Principal</label>
        <input
          type="text"
          value={formData.target_audience.location}
          onChange={(e) => handleNestedChange('target_audience', 'location', e.target.value)}
          placeholder="Ej: Chile, Santiago"
        />
      </div>

      <div className="form-group">
        <label>Intereses de la Audiencia</label>
        <div className="tag-input">
          <input
            type="text"
            value={newInterest}
            onChange={(e) => setNewInterest(e.target.value)}
            placeholder="Agregar interes"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInterest())}
          />
          <button type="button" onClick={addInterest}>+</button>
        </div>
        <div className="tags">
          {(formData.target_audience.interests || []).map((interest, i) => (
            <span key={i} className="tag">
              {interest}
              <button type="button" onClick={() => removeInterest(i)}>&times;</button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="setup-step">
      <h2>3. Contenido y Estilo Visual</h2>
      <p className="step-description">
        Define los pilares de contenido y lineamientos visuales.
      </p>

      <div className="form-group">
        <label>Pilares de Contenido</label>
        <p className="helper-text">
          Temas principales sobre los que habla tu marca (3-5 recomendados)
        </p>
        <div className="tag-input">
          <input
            type="text"
            value={newPillar}
            onChange={(e) => setNewPillar(e.target.value)}
            placeholder="Ej: Tips de uso, Testimonios, Detras de escenas"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('content_pillars', newPillar, setNewPillar))}
          />
          <button type="button" onClick={() => addToArray('content_pillars', newPillar, setNewPillar)}>
            +
          </button>
        </div>
        <div className="tags">
          {formData.content_pillars.map((pillar, i) => (
            <span key={i} className="tag tag-pillar">
              {pillar}
              <button type="button" onClick={() => removeFromArray('content_pillars', i)}>&times;</button>
            </span>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label>Keywords del Rubro</label>
        <div className="tag-input">
          <input
            type="text"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            placeholder="Palabras clave relevantes"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('keywords', newKeyword, setNewKeyword))}
          />
          <button type="button" onClick={() => addToArray('keywords', newKeyword, setNewKeyword)}>
            +
          </button>
        </div>
        <div className="tags">
          {formData.keywords.map((keyword, i) => (
            <span key={i} className="tag">
              {keyword}
              <button type="button" onClick={() => removeFromArray('keywords', i)}>&times;</button>
            </span>
          ))}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Color Primario</label>
          <div className="color-input">
            <input
              type="color"
              value={formData.visual_guidelines.primary_color}
              onChange={(e) => handleNestedChange('visual_guidelines', 'primary_color', e.target.value)}
            />
            <span>{formData.visual_guidelines.primary_color}</span>
          </div>
        </div>

        <div className="form-group">
          <label>Color Secundario</label>
          <div className="color-input">
            <input
              type="color"
              value={formData.visual_guidelines.secondary_color}
              onChange={(e) => handleNestedChange('visual_guidelines', 'secondary_color', e.target.value)}
            />
            <span>{formData.visual_guidelines.secondary_color}</span>
          </div>
        </div>
      </div>

      <div className="form-group">
        <label>Estilo Visual</label>
        <input
          type="text"
          value={formData.visual_guidelines.style}
          onChange={(e) => handleNestedChange('visual_guidelines', 'style', e.target.value)}
          placeholder="Ej: Minimalista, Colorido, Corporativo..."
        />
      </div>
    </div>
  );

  const renderStep4 = () => {
    const availableCompetitors = getAvailableCompetitors();

    return (
      <div className="setup-step">
        <h2>4. Competidores</h2>
        <p className="step-description">
          Selecciona cuentas del Instagram Tracker como competencia directa.
          Esto mejora las recomendaciones de CamDoBrain.
        </p>

        <div className="form-group">
          <label>Seleccionar Competidor</label>
          <div className="tag-input">
            <select
              value={selectedCompetitorId}
              onChange={(e) => setSelectedCompetitorId(e.target.value)}
              style={{ flex: 1, padding: '0.75rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}
            >
              <option value="">Seleccionar cuenta...</option>
              {availableCompetitors.map(acc => (
                <option key={acc.id} value={acc.id}>
                  @{acc.username} ({acc.followers_count?.toLocaleString() || 0} seguidores)
                </option>
              ))}
            </select>
            <button type="button" onClick={addCompetitor} disabled={!selectedCompetitorId}>
              + Agregar
            </button>
          </div>
          {availableTrackedAccounts.length === 0 && (
            <p className="helper-text" style={{ marginTop: '0.5rem' }}>
              No hay cuentas rastreadas disponibles.{' '}
              <a href="/onebase/instagram/tracker" style={{ color: '#8b5cf6' }}>
                Agrega cuentas en el Instagram Tracker
              </a>
            </p>
          )}
        </div>

        <div className="competitors-list">
          {competitors.length === 0 ? (
            <div className="empty-competitors">
              <p>No has seleccionado competidores aun.</p>
              <p className="helper-text">
                Selecciona 3-5 competidores directos para mejorar las predicciones.
              </p>
            </div>
          ) : (
            competitors.map(comp => (
              <div key={comp.id} className="competitor-item">
                <div className="competitor-info">
                  <span className="competitor-username">@{comp.username}</span>
                  {(comp.followers_count || 0) > 0 && (
                    <span className="competitor-followers">
                      {comp.followers_count.toLocaleString()} seguidores
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeCompetitor(comp.id)}
                >
                  &times;
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="camdobrain-setup">
      <div className="setup-header">
        <h1>
          {isEditing ? 'Editar Perfil de Marca' : 'Configurar Nueva Marca'}
        </h1>
      </div>

      {/* Progress Steps */}
      <div className="progress-steps">
        {[1, 2, 3, 4].map(s => (
          <div
            key={s}
            className={`progress-step ${step === s ? 'active' : ''} ${step > s ? 'completed' : ''}`}
            onClick={() => setStep(s)}
          >
            <div className="step-number">{step > s ? '✓' : s}</div>
            <div className="step-label">
              {s === 1 && 'Basico'}
              {s === 2 && 'Audiencia'}
              {s === 3 && 'Contenido'}
              {s === 4 && 'Competencia'}
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="setup-form">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
      </div>

      <div className="setup-actions">
        {step > 1 && (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setStep(step - 1)}
          >
            ← Anterior
          </button>
        )}

        {step < 4 ? (
          <button
            type="button"
            className="estado-btn"
            onClick={() => setStep(step + 1)}
            disabled={step === 1 && (!formData.account_id || !formData.brand_name)}
          >
            Siguiente →
          </button>
        ) : (
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Guardando...' : (isEditing ? 'Guardar Cambios' : 'Crear Perfil')}
          </button>
        )}
      </div>
    </div>
  );
};

export default CamDoBrainSetup;
